import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InventoryIcon from "@mui/icons-material/Inventory";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CategoryIcon from "@mui/icons-material/Category";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import Loader from "@jumbo/components/Loader";
import { getAnalyticsDashboard } from "app/redux/slice/dashboardSlice";
import { userType } from "app/helpers/userType";

// ─── Constants ───────────────────────────────────────────────────────────────

const PLATFORM_COLORS = {
  flipkart: "#2874F0",
  meesho: "#F43397",
  myntra: "#FF3F6C",
};

const CATEGORY_PALETTE = [
  "#2874F0", "#FF3F6C", "#F43397", "#0F9D58", "#FF6F00",
  "#8E24AA", "#00ACC1", "#E53935", "#43A047", "#FB8C00",
];

const QUICK_RANGES = [
  {
    key: "7",
    label: "7D",
    getDates: () => ({
      fromDate: moment().subtract(6, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    }),
  },
  {
    key: "30",
    label: "30D",
    getDates: () => ({
      fromDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    }),
  },
  {
    key: "90",
    label: "90D",
    getDates: () => ({
      fromDate: moment().subtract(89, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    }),
  },
  {
    key: "year",
    label: "This Year",
    getDates: () => ({
      fromDate: moment().startOf("year").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    }),
  },
];

// ─── Formatters ──────────────────────────────────────────────────────────────

const formatCurrency = (amount = 0) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);

const formatCompactNumber = (value = 0) =>
  new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value || 0);

const formatNumber = (value = 0) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value || 0);

const signedCurrency = (amount = 0) =>
  `${amount >= 0 ? "+" : "-"}${formatCurrency(Math.abs(amount))}`;

const buildDefaultFilters = () => ({
  fromDate: moment().subtract(89, "days").format("YYYY-MM-DD"),
  toDate: moment().format("YYYY-MM-DD"),
  platform: "all",
  categoryId: "",
  productId: "",
});

// ─── Shared Components ────────────────────────────────────────────────────────

const SectionCard = ({ title, subtitle, children, action, sx }) => (
  <Card sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, ...sx }}>
    <CardContent sx={{ p: 2.5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const MetricCard = ({ title, value, subtitle, accent, tone = "default", icon }) => (
  <Card
    sx={{
      height: "100%",
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      background:
        tone === "profit"
          ? `linear-gradient(135deg, ${alpha("#097969", 0.12)}, ${alpha("#097969", 0.02)})`
          : tone === "loss"
          ? `linear-gradient(135deg, ${alpha("#D32F2F", 0.12)}, ${alpha("#D32F2F", 0.02)})`
          : `linear-gradient(135deg, ${alpha(accent || "#2874F0", 0.08)}, rgba(255,255,255,0.8))`,
    }}
  >
    <CardContent sx={{ p: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        {icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(accent || "#2874F0", 0.12),
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            {React.cloneElement(icon, { sx: { color: accent || "primary.main", fontSize: 20 } })}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: "0.78rem" }}>
            {title}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, mb: 0.25, color: accent || "text.primary", lineHeight: 1.2 }}
          >
            {value}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
          ) : null}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const RiskChip = ({ value }) => {
  const chipProps = {
    healthy: { label: "Healthy", color: "success" },
    warning: { label: "Warning", color: "warning" },
    critical: { label: "Critical", color: "error" },
    out_of_stock: { label: "Out of Stock", color: "error" },
  };
  const config = chipProps[value] || chipProps.warning;
  return <Chip size="small" label={config.label} color={config.color} />;
};

// ─── Smart Alerts ─────────────────────────────────────────────────────────────

const SmartAlerts = ({ overview, platformComparison, inventoryRisks, highReturnProducts }) => {
  const [open, setOpen] = useState(true);

  const alerts = useMemo(() => {
    const list = [];

    const outOfStock = (inventoryRisks || []).filter((r) => r.riskLevel === "out_of_stock").length;
    const criticalStock = (inventoryRisks || []).filter((r) => r.riskLevel === "critical").length;

    if (outOfStock > 0) {
      list.push({
        severity: "error",
        key: "stockout",
        title: `${outOfStock} product${outOfStock > 1 ? "s" : ""} out of stock`,
        detail: "Immediate restocking required to prevent lost sales.",
      });
    }
    if (criticalStock > 0) {
      list.push({
        severity: "warning",
        key: "critical_stock",
        title: `${criticalStock} product${criticalStock > 1 ? "s" : ""} at critical stock level`,
        detail: "Less than 7 days of inventory remaining.",
      });
    }

    const highReturnCount = (highReturnProducts || []).filter(
      (p) => (p.returnRate || 0) > 50
    ).length;
    if (highReturnCount > 0) {
      list.push({
        severity: "error",
        key: "high_return",
        title: `${highReturnCount} product${highReturnCount > 1 ? "s" : ""} with return rate above 50%`,
        detail: "High returns are eroding profit. Review sizing, quality, or listings.",
      });
    }

    const lossPlatforms = (platformComparison || []).filter((p) => (p.finalProfit || 0) < 0);
    if (lossPlatforms.length > 0) {
      list.push({
        severity: "warning",
        key: "platform_loss",
        title: `${lossPlatforms.map((p) => p.platform).join(", ")} reporting a loss`,
        detail: "Marketing costs or returns may be eating into platform profit.",
      });
    }

    const pendingAmount = overview?.pendingAmount || 0;
    if (pendingAmount > 0) {
      list.push({
        severity: "info",
        key: "pending",
        title: `${formatCurrency(pendingAmount)} in pending settlements`,
        detail: "Follow up with marketplaces to expedite payment release.",
      });
    }

    return list;
  }, [overview, platformComparison, inventoryRisks, highReturnProducts]);

  if (alerts.length === 0) return null;

  const errorCount = alerts.filter((a) => a.severity === "error").length;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1,
          borderRadius: open ? "12px 12px 0 0" : 2,
          bgcolor: errorCount > 0 ? alpha("#D32F2F", 0.06) : alpha("#FF6F00", 0.06),
          border: "1px solid",
          borderColor: errorCount > 0 ? alpha("#D32F2F", 0.2) : alpha("#FF6F00", 0.2),
          borderBottom: open ? "none" : undefined,
          cursor: "pointer",
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ErrorOutlineIcon
            sx={{ color: errorCount > 0 ? "error.main" : "warning.main", fontSize: 20 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {alerts.length} Action Item{alerts.length > 1 ? "s" : ""} Require Attention
          </Typography>
          {errorCount > 0 && (
            <Chip
              label={`${errorCount} Critical`}
              color="error"
              size="small"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          )}
        </Stack>
        <IconButton size="small">
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>

      <Collapse in={open}>
        <Box
          sx={{
            border: "1px solid",
            borderColor: errorCount > 0 ? alpha("#D32F2F", 0.2) : alpha("#FF6F00", 0.2),
            borderTop: "none",
            borderRadius: "0 0 12px 12px",
            overflow: "hidden",
          }}
        >
          {alerts.map((alert, idx) => (
            <Alert
              key={alert.key}
              severity={alert.severity}
              icon={false}
              sx={{
                borderRadius: 0,
                borderBottom: idx < alerts.length - 1 ? "1px solid" : "none",
                borderColor: "divider",
                py: 0.75,
              }}
            >
              <AlertTitle sx={{ fontSize: "0.82rem", mb: 0.25, fontWeight: 700 }}>
                {alert.title}
              </AlertTitle>
              <Typography variant="caption" color="text.secondary">
                {alert.detail}
              </Typography>
            </Alert>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Platform Health Row ──────────────────────────────────────────────────────

const PlatformHealthCard = ({ platform }) => {
  const isLoss = (platform.finalProfit || 0) < 0;
  const color = PLATFORM_COLORS[platform.platformKey] || "#666";

  return (
    <Card
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        borderTop: `3px solid ${color}`,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {platform.platform}
          </Typography>
          <Chip
            size="small"
            label={isLoss ? "Loss" : "Profit"}
            color={isLoss ? "error" : "success"}
            sx={{ ml: "auto", height: 20, fontSize: "0.7rem" }}
          />
        </Stack>

        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Revenue
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color }}>
              {formatCurrency(platform.grossRevenue)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Profit / Loss
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: isLoss ? "error.main" : "success.main" }}
            >
              {signedCurrency(platform.finalProfit)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Margin
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: isLoss ? "error.main" : "success.main" }}
            >
              {platform.profitMargin ?? 0}%
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Return Rate
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: (platform.returnRate || 0) > 30 ? "error.main" : "text.primary",
              }}
            >
              {platform.returnRate || 0}%
            </Typography>
          </Grid>
        </Grid>

        {(platform.pendingAmount || 0) > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <PendingActionsIcon sx={{ fontSize: 14, color: "warning.main" }} />
            <Typography variant="caption" color="text.secondary">
              {formatCurrency(platform.pendingAmount)} pending
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Category Performance ─────────────────────────────────────────────────────

const CustomBarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 1.5,
        minWidth: 180,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {d.categoryName}
      </Typography>
      <Typography variant="caption" display="block">
        Revenue: {formatCurrency(d.grossRevenue)}
      </Typography>
      <Typography variant="caption" display="block">
        Profit: {formatCurrency(d.finalProfit)}
      </Typography>
      <Typography variant="caption" display="block">
        Return Rate: {d.returnRate}%
      </Typography>
      <Typography variant="caption" display="block">
        Margin: {d.profitMargin}%
      </Typography>
    </Box>
  );
};

const CategoryPerformance = ({ categoryBreakdown }) => {
  const data = (categoryBreakdown || []).slice(0, 8);
  const maxRevenue = Math.max(...data.map((d) => d.grossRevenue), 1);

  if (data.length === 0) return null;

  return (
    <SectionCard
      title="Category Performance"
      subtitle="Revenue, profitability, and return rate broken down by product category."
      sx={{ mb: 2.5 }}
      action={
        <Chip
          icon={<CategoryIcon sx={{ fontSize: "14px !important" }} />}
          label={`${data.length} categories`}
          size="small"
          variant="outlined"
        />
      }
    >
      <Grid container spacing={3}>
        {/* Bar Chart */}
        <Grid item xs={12} lg={7}>
          <Box sx={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                layout="vertical"
                margin={{ left: 8, right: 20, top: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tickFormatter={formatCompactNumber} tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="categoryName"
                  width={110}
                  tick={{ fontSize: 11 }}
                />
                <RTooltip content={<CustomBarTooltip />} />
                <Bar dataKey="grossRevenue" name="Revenue" radius={[0, 4, 4, 0]}>
                  {data.map((entry, index) => (
                    <Cell
                      key={entry.categoryId || index}
                      fill={CATEGORY_PALETTE[index % CATEGORY_PALETTE.length]}
                    />
                  ))}
                </Bar>
                <Bar dataKey="finalProfit" name="Profit" radius={[0, 4, 4, 0]} fill="#0F9D58" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        {/* Category Table */}
        <Grid item xs={12} lg={5}>
          <TableContainer sx={{ maxHeight: 280, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Category</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    Revenue
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    Ret%
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                    Margin
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, idx) => (
                  <TableRow key={row.categoryId || idx} hover>
                    <TableCell sx={{ maxWidth: 120 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: CATEGORY_PALETTE[idx % CATEGORY_PALETTE.length],
                            flexShrink: 0,
                          }}
                        />
                        <Box>
                          <Tooltip title={row.categoryName}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 100,
                              }}
                            >
                              {row.categoryName}
                            </Typography>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary">
                            {formatNumber(row.orders)} orders
                          </Typography>
                        </Box>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={(row.grossRevenue / maxRevenue) * 100}
                        sx={{
                          mt: 0.5,
                          height: 3,
                          borderRadius: 2,
                          bgcolor: alpha(CATEGORY_PALETTE[idx % CATEGORY_PALETTE.length], 0.15),
                          "& .MuiLinearProgress-bar": {
                            bgcolor: CATEGORY_PALETTE[idx % CATEGORY_PALETTE.length],
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        {formatCurrency(row.grossRevenue)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: row.finalProfit >= 0 ? "success.main" : "error.main" }}
                      >
                        {signedCurrency(row.finalProfit)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: (row.returnRate || 0) > 30 ? "error.main" : "text.primary",
                        }}
                      >
                        {row.returnRate}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: (row.profitMargin || 0) < 0 ? "error.main" : "success.main",
                        }}
                      >
                        {row.profitMargin}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </SectionCard>
  );
};

// ─── Platform Donut ────────────────────────────────────────────────────────────

const PlatformDonutTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {d.platform}
      </Typography>
      <Typography variant="caption" display="block">
        Revenue: {formatCurrency(d.grossRevenue)}
      </Typography>
      <Typography variant="caption" display="block">
        Share: {d.revenueShare}%
      </Typography>
    </Box>
  );
};

// ─── Main Dashboard ────────────────────────────────────────────────────────────

const AnalyticsDashboard = () => {
  const dispatch = useDispatch();
  const dashboardState = useSelector((state) => state?.dashboard);
  const analytics = dashboardState?.data;
  const loading = dashboardState?.loading;

  const [filters, setFilters] = useState(buildDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(buildDefaultFilters);
  const [activeQuickKey, setActiveQuickKey] = useState("90");

  useEffect(() => {
    dispatch(getAnalyticsDashboard(appliedFilters));
  }, [appliedFilters, dispatch]);

  const filterOptions = analytics?.filterOptions || {};
  const productOptions = (filterOptions?.products || []).filter(
    (product) => !filters.categoryId || product.categoryId === filters.categoryId
  );
  const selectedProduct =
    productOptions.find((product) => product.value === filters.productId) || null;

  const overview = analytics?.overview || {};
  const platformComparison = analytics?.platformComparison || [];
  const revenueTrend = analytics?.revenueTrend || [];
  const returnTrend = analytics?.returnBehavior?.trend || [];
  const topProducts = analytics?.topProducts || [];
  const highReturnProducts = analytics?.highReturnProducts || [];
  const inventoryRisks = analytics?.inventoryRisks || [];
  const salesForecast = analytics?.salesForecast || {};
  const categoryBreakdown = analytics?.categoryBreakdown || [];

  // Donut data: platform revenue share
  const totalRevenue = platformComparison.reduce((s, p) => s + (p.grossRevenue || 0), 0);
  const donutData = platformComparison.map((p) => ({
    ...p,
    revenueShare:
      totalRevenue > 0 ? +((p.grossRevenue / totalRevenue) * 100).toFixed(1) : 0,
  }));

  const isProfitPositive = (overview.finalProfit || 0) >= 0;

  const handleApply = () => setAppliedFilters(filters);

  const handleReset = () => {
    const defaults = buildDefaultFilters();
    setFilters(defaults);
    setAppliedFilters(defaults);
    setActiveQuickKey("90");
  };

  const setQuickRange = (range) => {
    setActiveQuickKey(range.key);
    setFilters((prev) => ({ ...prev, ...range.getDates() }));
  };

  return (
    <Box>
      {loading ? <Loader /> : null}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          mb: 2.5,
          p: 2.5,
          borderRadius: 4,
          background:
            "linear-gradient(135deg, rgba(40,116,240,0.12), rgba(255,63,108,0.08), rgba(244,51,151,0.04))",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "flex-start" }}
          spacing={1.5}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
              Central Commerce Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Revenue, returns, inventory risk, and profitability across all platforms in one place.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              Last updated:{" "}
              {analytics?.lastUpdatedAt
                ? moment(analytics.lastUpdatedAt).format("DD MMM YYYY, hh:mm A")
                : "-"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Chip
              label="Myntra"
              size="small"
              sx={{ bgcolor: PLATFORM_COLORS.myntra, color: "white", fontWeight: 600 }}
            />
            <Chip
              label="Meesho"
              size="small"
              sx={{ bgcolor: PLATFORM_COLORS.meesho, color: "white", fontWeight: 600 }}
            />
            <Chip
              label="Flipkart"
              size="small"
              sx={{ bgcolor: PLATFORM_COLORS.flipkart, color: "white", fontWeight: 600 }}
            />
          </Stack>
        </Stack>
      </Box>

      {/* ── Smart Alerts ────────────────────────────────────────────────────── */}
      {analytics && (
        <SmartAlerts
          overview={overview}
          platformComparison={platformComparison}
          inventoryRisks={inventoryRisks}
          highReturnProducts={highReturnProducts}
        />
      )}

      {/* ── Filters ──────────────────────────────────────────────────────────── */}
      <SectionCard
        title="Filters"
        subtitle="Filter by date, platform, category, or product."
        sx={{ mb: 2.5 }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={2.2}>
            <TextField
              fullWidth type="date" size="small" label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.fromDate}
              onChange={(e) => setFilters((p) => ({ ...p, fromDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={2.2}>
            <TextField
              fullWidth type="date" size="small" label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.toDate}
              onChange={(e) => setFilters((p) => ({ ...p, toDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select fullWidth size="small" label="Platform"
              value={filters.platform}
              onChange={(e) => setFilters((p) => ({ ...p, platform: e.target.value }))}
            >
              {(filterOptions?.platforms || []).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2.3}>
            <TextField
              select fullWidth size="small" label="Category"
              value={filters.categoryId}
              onChange={(e) =>
                setFilters((p) => ({ ...p, categoryId: e.target.value, productId: "" }))
              }
            >
              <MenuItem value="">All Categories</MenuItem>
              {(filterOptions?.categories || []).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3.3}>
            <Autocomplete
              size="small" options={productOptions} value={selectedProduct}
              onChange={(_, v) => setFilters((p) => ({ ...p, productId: v?.value || "" }))}
              getOptionLabel={(opt) => opt?.label || ""}
              renderInput={(params) => (
                <TextField {...params} label="Product" placeholder="All Products" />
              )}
            />
          </Grid>
        </Grid>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ mt: 2 }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {QUICK_RANGES.map((range) => (
              <Button
                key={range.key}
                size="small"
                variant={activeQuickKey === range.key ? "contained" : "outlined"}
                onClick={() => setQuickRange(range)}
              >
                {range.key === "year" ? range.label : `Last ${range.label}`}
              </Button>
            ))}
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={handleReset} disabled={loading}>Reset</Button>
            <Button
              variant="contained" onClick={handleApply} disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </SectionCard>

      {!analytics && !loading ? (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          No analytics data is available yet.
        </Alert>
      ) : null}

      {/* ── Overview Metrics ──────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} lg={2}>
          <MetricCard
            title="Gross Revenue"
            value={formatCurrency(overview.grossRevenue)}
            subtitle={`${formatNumber(overview.orders)} orders`}
            accent="#2874F0"
            icon={<MonetizationOnIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2}>
          <MetricCard
            title="Received Revenue"
            value={formatCurrency(overview.receivedRevenue)}
            subtitle={`${formatCurrency(overview.pendingAmount)} pending`}
            accent="#0F9D58"
            icon={<AccountBalanceIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2}>
          <MetricCard
            title="Final Profit"
            value={signedCurrency(overview.finalProfit)}
            subtitle={`${formatCurrency(overview.totalCost)} total cost`}
            accent={isProfitPositive ? "#0F9D58" : "#D32F2F"}
            tone={isProfitPositive ? "profit" : "loss"}
            icon={isProfitPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2}>
          <MetricCard
            title="Return Rate"
            value={`${overview.returnRate || 0}%`}
            subtitle={`${formatNumber(overview.returnedUnits)} returned units`}
            accent="#FF6F00"
            icon={<AssignmentReturnIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2}>
          <MetricCard
            title="Low Stock Alerts"
            value={formatNumber(overview.lowStockCount)}
            subtitle={`${formatNumber(overview.stockoutRiskCount)} stock-out risks`}
            accent="#8E24AA"
            icon={<WarningAmberIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2}>
          <MetricCard
            title="Inventory Value"
            value={formatCurrency(overview.inventoryValue)}
            subtitle={`${formatNumber(overview.deliveredUnits)} units delivered`}
            accent="#5D4037"
            icon={<InventoryIcon />}
          />
        </Grid>
      </Grid>

      {/* ── Platform Health ───────────────────────────────────────────────────── */}
      {platformComparison.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {platformComparison.map((platform) => (
            <Grid item xs={12} sm={4} key={platform.platformKey}>
              <PlatformHealthCard platform={platform} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Revenue Trend + Platform Revenue Share ────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} lg={8}>
          <SectionCard
            title="Revenue Trend"
            subtitle="Daily revenue and profit movement across selected platforms."
            sx={{ height: "100%" }}
          >
            <Box sx={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <AreaChart data={revenueTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" minTickGap={24} />
                  <YAxis tickFormatter={formatCompactNumber} />
                  <RTooltip
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label, payload) =>
                      payload?.[0]?.payload?.date
                        ? moment(payload[0].payload.date).format("DD MMM YYYY")
                        : label
                    }
                  />
                  <Legend />
                  <Area
                    type="monotone" dataKey="totalRevenue"
                    stroke="#2874F0" fill="#2874F0" fillOpacity={0.15} name="Revenue"
                  />
                  <Area
                    type="monotone" dataKey="totalProfit"
                    stroke="#0F9D58" fill="#0F9D58" fillOpacity={0.12} name="Profit"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={4}>
          <SectionCard
            title="Revenue Share by Platform"
            subtitle="Proportion of gross revenue contributed per marketplace."
            sx={{ height: "100%" }}
          >
            <Box sx={{ width: "100%", height: 180 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={donutData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="grossRevenue"
                  >
                    {donutData.map((entry) => (
                      <Cell
                        key={entry.platformKey}
                        fill={PLATFORM_COLORS[entry.platformKey] || "#999"}
                      />
                    ))}
                  </Pie>
                  <RTooltip content={<PlatformDonutTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {donutData.map((p) => (
                <Stack key={p.platformKey} direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: PLATFORM_COLORS[p.platformKey] || "#999",
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {p.platform}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {p.revenueShare}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(p.grossRevenue)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {/* ── Orders vs Returns + Forecast ─────────────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} lg={7}>
          <SectionCard
            title="Orders vs Returns"
            subtitle="Daily order volume compared with returns."
            sx={{ height: "100%" }}
          >
            <Box sx={{ width: "100%", height: 270 }}>
              <ResponsiveContainer>
                <LineChart data={returnTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" minTickGap={24} />
                  <YAxis />
                  <RTooltip
                    labelFormatter={(label, payload) =>
                      payload?.[0]?.payload?.date
                        ? moment(payload[0].payload.date).format("DD MMM YYYY")
                        : label
                    }
                  />
                  <Legend />
                  <Line type="monotone" dataKey="orders" stroke="#2874F0" strokeWidth={2.5} name="Orders" dot={false} />
                  <Line type="monotone" dataKey="returns" stroke="#FF6F00" strokeWidth={2.5} name="Returns" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={5}>
          <SectionCard
            title="Forecast & Inventory Pulse"
            subtitle="Short-horizon projections and risk signals."
            sx={{ height: "100%" }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Avg Daily Revenue</Typography>
                <Typography fontWeight={700}>{formatCurrency(salesForecast.avgDailyRevenue)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Projected 7D Revenue</Typography>
                <Typography fontWeight={700}>{formatCurrency(salesForecast.projectedRevenue7d)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Projected 30D Revenue</Typography>
                <Typography fontWeight={700}>{formatCurrency(salesForecast.projectedRevenue30d)}</Typography>
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Avg Daily Units</Typography>
                <Typography fontWeight={700}>{formatNumber(salesForecast.avgDailyUnits)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Projected 7D Units</Typography>
                <Typography fontWeight={700}>{formatNumber(salesForecast.projectedUnits7d)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Projected 30D Units</Typography>
                <Typography fontWeight={700}>{formatNumber(salesForecast.projectedUnits30d)}</Typography>
              </Stack>
              {platformComparison.length > 0 && (
                <>
                  <Divider />
                  {platformComparison.map((platform) => (
                    <Stack
                      key={platform.platformKey}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 8, height: 8, borderRadius: "50%",
                            bgcolor: PLATFORM_COLORS[platform.platformKey] || "grey.400",
                          }}
                        />
                        <Typography color="text.secondary">{platform.platform}</Typography>
                      </Stack>
                      <Typography
                        fontWeight={700}
                        sx={{ color: PLATFORM_COLORS[platform.platformKey] }}
                      >
                        {formatCurrency(platform.grossRevenue)}
                      </Typography>
                    </Stack>
                  ))}
                </>
              )}
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {/* ── Category Performance ──────────────────────────────────────────────── */}
      <CategoryPerformance categoryBreakdown={categoryBreakdown} />

      {/* ── Top Products + High Return Products ───────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} lg={6}>
          <SectionCard
            title="Top-Selling Products"
            subtitle="Best movers by delivered units and revenue."
            sx={{ height: "100%" }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Units</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Profit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topProducts.length > 0 ? (
                    topProducts.map((item) => (
                      <TableRow key={item.key}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item.productName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(item.platforms || []).join(", ")} • {item.categoryName || "Unmapped"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(item.deliveredUnits)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.grossRevenue)}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: item.finalProfit >= 0 ? "#0F9D58" : "#D32F2F", fontWeight: 700 }}
                        >
                          {signedCurrency(item.finalProfit)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No product sales found for this selection.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <SectionCard
            title="High Return Products"
            subtitle="Products creating return pressure and profit drag."
            sx={{ height: "100%" }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Returned</TableCell>
                    <TableCell align="right">Return %</TableCell>
                    <TableCell align="right">Profit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {highReturnProducts.length > 0 ? (
                    highReturnProducts.map((item) => (
                      <TableRow key={item.key}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item.productName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(item.platforms || []).join(", ")}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(item.returnedUnits)}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ color: (item.returnRate || 0) > 50 ? "error.main" : "text.primary", fontWeight: 600 }}
                          >
                            {item.returnRate}%
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: item.finalProfit >= 0 ? "#0F9D58" : "#D32F2F", fontWeight: 700 }}
                        >
                          {signedCurrency(item.finalProfit)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No return-heavy products found for this selection.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Grid>
      </Grid>

      {/* ── Inventory Risks ───────────────────────────────────────────────────── */}
      <SectionCard
        title="Inventory Risks & Reorder Suggestions"
        subtitle="Low stock alerts based on current inventory and recent sales velocity."
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell align="right">Daily Velocity</TableCell>
                <TableCell align="right">Days Left</TableCell>
                <TableCell align="right">Reorder Qty</TableCell>
                <TableCell align="right">Inventory Value</TableCell>
                <TableCell align="right">Risk</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventoryRisks.length > 0 ? (
                inventoryRisks.map((item) => (
                  <TableRow key={`${item.productId}-${item.variantId || "base"}`}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {item.productName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.variantName || "Base Product"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatNumber(item.currentStock)}</TableCell>
                    <TableCell align="right">{formatNumber(item.avgDailyUnits)}</TableCell>
                    <TableCell align="right">
                      {item.daysToStockout == null ? "-" : item.daysToStockout}
                    </TableCell>
                    <TableCell align="right">{formatNumber(item.reorderQty)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.inventoryValue)}</TableCell>
                    <TableCell align="right">
                      <RiskChip value={item.riskLevel} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No inventory risks detected for this selection.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>
    </Box>
  );
};

// ─── Entry ─────────────────────────────────────────────────────────────────────

const hasDashboardPermission = () => {
  if (userType().isMA) return true;
  const permissions = JSON.parse(localStorage.getItem("permission"));
  return (
    Array.isArray(permissions) &&
    permissions.some((perm) => perm?.name === "dashboard")
  );
};

const Welcome = () => {
  const user = JSON.parse(localStorage.getItem("User"));
  return (
    <Card>
      <CardContent sx={{ pt: 4, textAlign: "center" }}>
        <Typography variant="h2" fontWeight={700}>
          Welcome{user?.name ? `, ${user.name}` : ""}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Users without the dashboard permission only get a welcome screen, so the
// analytics API is never called for them.
const Home = () =>
  hasDashboardPermission() ? <AnalyticsDashboard /> : <Welcome />;

export default Home;
